import React from "react";
// ANT DESIGN COMPONENTS
import { Tag } from "antd";
// DEVICE DETECT
import { isMobile } from "react-device-detect";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBan,
  faCheckCircle,
  faCircleExclamation,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector } from "react-redux";
// COMPONENTS
import DrawerRowDivider from "./DrawerRowDivider";
// INTERFACES
import { RootState } from "../../../app/store";
import { hasUserStatus, toUserStatus } from "../../../utils/userStatus";
import { UserStatus } from "../../../flatbuffers/generated/archive/user-status";

// safeText guards against the "Objects are not valid as a React child"
// error: if an upstream code path puts an unexpected object (e.g. a
// FlatBuffer-decoded {value: ...} wrapper) onto userLogged, this coerces
// it to a string and logs once so the offending field is visible.
function safeText(value: unknown, fieldName: string): string {
  if (value === null || value === undefined || value === "") return "--";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  // eslint-disable-next-line no-console
  console.warn(`DrawerUserInformation: field "${fieldName}" is not a primitive`, value);
  try {
    return JSON.stringify(value);
  } catch {
    return "--";
  }
}

function DrawerUserInformation({ data }: any) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const { theme } = useSelector((state: RootState) => state.home);
  const [t] = useTranslation("global");
  const status = toUserStatus(data?.status);

  // ************************************************ */
  // USE STATE VARIABLES **************************** */

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // FUNCTIONS ************************************** */

  // ************************************************* */
  // USE EFFECT ************************************** */

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      <br />
      {/** ************************************************* */}
      {/** COMPONENT DRAWER DIVIDER */}
      <DrawerRowDivider icon={faUser} title={t("userManagement.userInformation")} />
      {/** ************************************************* */}
      {/** USER INFORMATION */}
      <div className={`drawer__box__${theme}`}>
        {/* USER LOGGED ID */}
        <div className="generalStyles__flex">
          <div>
            <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>
              {t("userManagement.userId")}
            </p>
          </div>
          <div className="drawer__textContainer">
            <p className="drawer__text">{safeText(data.id, "id")}</p>
          </div>
        </div>
        {/* FIRST NAME */}
        <div className="generalStyles__flex">
          <div>
            <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>
              {t("userManagement.firstName")}
            </p>
          </div>
          <div className="drawer__textContainer">
            <p className="drawer__text">{safeText(data.first_name, "first_name")}</p>
          </div>
        </div>
        {/* MIDDLE NAME */}
        <div className="generalStyles__flex">
          <div>
            <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>
              {t("userManagement.middleName")}
            </p>
          </div>
          <div className="drawer__textContainer">
            <p className="drawer__text">{safeText(data.middle_name, "middle_name")}</p>
          </div>
        </div>
        {/* LAST NAME */}
        <div className="generalStyles__flex">
          <div>
            <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>
              {t("userManagement.lastName")}
            </p>
          </div>
          <div className="drawer__textContainer">
            <p className="drawer__text">{safeText(data.last_name, "last_name")}</p>
          </div>
        </div>
        {/* EMAIL */}
        <div className="generalStyles__flex">
          <div>
            <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>
              {t("userManagement.email")}
            </p>
          </div>
          <div className="drawer__textContainer">
            <p className="drawer__text">{safeText(data.email, "email")}</p>
          </div>
        </div>
        {/* ADDRESS */}
        <div className="generalStyles__flex">
          <div>
            <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>
              {t("userManagement.address")}
            </p>
          </div>
          <div className="drawer__textContainer">
            <p className="drawer__text">
              {safeText(data.contact_info?.[0]?.address, "contact_info[0].address")}
            </p>
          </div>
        </div>
        {/* PHONE */}
        <div className="generalStyles__flex">
          <div>
            <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>
              {t("userManagement.phone")}
            </p>
          </div>
          <div className="drawer__textContainer">
            <p className="drawer__text">
              {safeText(data.contact_info?.[0]?.phone, "contact_info[0].phone")}
            </p>
          </div>
        </div>
        {/* PHONE CODE */}
        <div className="generalStyles__flex">
          <div>
            <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>
              {t("userManagement.dialCode")}
            </p>
          </div>
          <div className="drawer__textContainer">
            <p className="drawer__text">
              {safeText(data.contact_info?.[0]?.phone_code, "contact_info[0].phone_code")}
            </p>
          </div>
        </div>
        {/* STATUS */}
        <div className="generalStyles__flex">
          <div>
            <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>
              {t("userManagement.status")}
            </p>
          </div>
          <div className="drawer__textContainer">
            <p className="drawer__text">
              {hasUserStatus(data.status) ? (
                <>
                  {status === UserStatus.ACTIVE && (
                    <Tag
                      color="success"
                      icon={
                        <FontAwesomeIcon icon={faCheckCircle} className="generalStyles__mrFix" />
                      }
                      className="drawer__tag"
                    >
                      Active
                    </Tag>
                  )}
                  {status === UserStatus.NEW && (
                    <Tag
                      color="warning"
                      icon={
                        <FontAwesomeIcon
                          icon={faCircleExclamation}
                          className="generalStyles__mrFix"
                        />
                      }
                      className="drawer__tag"
                    >
                      New user
                    </Tag>
                  )}
                  {status === UserStatus.BLOCKED && (
                    <Tag
                      color="error"
                      icon={<FontAwesomeIcon icon={faBan} className="generalStyles__mrFix" />}
                      className="drawer__tag"
                    >
                      Inactive
                    </Tag>
                  )}
                </>
              ) : (
                "--"
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DrawerUserInformation;
